/**
 * Helper functions for working with V2 Note and KeySignature objects
 *
 * This module provides UI-to-V2 conversion utilities for components that work
 * with Note and KeySignature objects. These helpers bridge the gap between
 * user-facing modifier names and the abbreviated V2 format codes.
 *
 * @module noteHelpers
 */
// ============================================================================
// Conversion Functions
// ============================================================================
/**
 * Convert UI ModifierType to V2 ModifierCode
 *
 * Maps user-facing modifier names to abbreviated codes used in Note objects.
 *
 * @param modifier - UI modifier type
 * @returns V2 modifier code
 *
 * @example
 * modifierToModifierCode('sharp') // Returns 's'
 * modifierToModifierCode('flat')  // Returns 'f'
 * modifierToModifierCode('natural') // Returns 'n'
 */
export function modifierToModifierCode(modifier) {
    switch (modifier) {
        case 'natural':
            return 'n';
        case 'flat':
            return 'f';
        case 'sharp':
            return 's';
    }
}
/**
 * Convert V2 ModifierCode to UI ModifierType
 *
 * Maps abbreviated codes from Note objects to user-facing modifier names.
 *
 * @param code - V2 modifier code
 * @returns UI modifier type
 *
 * @example
 * modifierCodeToModifierType('s') // Returns 'sharp'
 * modifierCodeToModifierType('f') // Returns 'flat'
 * modifierCodeToModifierType('n') // Returns 'natural'
 */
export function modifierCodeToModifierType(code) {
    switch (code) {
        case 'n':
            return 'natural';
        case 'f':
            return 'flat';
        case 's':
            return 'sharp';
    }
}
// ============================================================================
// Helper Functions
// ============================================================================
/**
 * Build a Note object from UI component state
 *
 * Convenience function for creating Note objects from separate
 * note name and modifier selections in UI components.
 *
 * @param noteName - Note name (C-B)
 * @param modifier - UI modifier type
 * @returns V2 Note object
 *
 * @example
 * buildNote('F', 'sharp') // Returns { noteName: 'F', modifier: 's' }
 * buildNote('B', 'flat')  // Returns { noteName: 'B', modifier: 'f' }
 */
export function buildNote(noteName, modifier) {
    return {
        noteName,
        modifier: modifierToModifierCode(modifier),
    };
}
/**
 * Extract UI modifier type from a Note object
 *
 * Convenience function for initializing UI component state from Note objects.
 *
 * @param note - V2 Note object
 * @returns UI modifier type
 *
 * @example
 * extractModifier({ noteName: 'F', modifier: 's' }) // Returns 'sharp'
 * extractModifier({ noteName: 'B', modifier: 'f' }) // Returns 'flat'
 */
export function extractModifier(note) {
    return modifierCodeToModifierType(note.modifier);
}
